import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ComplianceReport } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { ErrorPanel } from '../../components/ErrorPanel';
import { ExportButton } from '../../components/ExportButton';
import { Badge, EmptyRow, FilterBar, PageHeader, Pager, SelectField, Spinner, TableWrap, TextField, Th } from '../../components/ui';
import { COMPLIANCE, daysUntil, formatDay, useGroupOptions } from './api';
import { STATE_TONE } from './MemberDocumentsTab';

const PAGE_SIZE = 25;
type State = 'all' | 'problems' | 'expiring';

/** Manage -> Compliance report: who is missing or about to lose a document, with export (contract compliance s6). */
export function ComplianceReportPage() {
  const groups = useGroupOptions();
  const [groupId, setGroupId] = useState('');
  const [state, setState] = useState<State>('problems');
  const [withinDays, setWithinDays] = useState(30);
  const [pageNo, setPageNo] = useState(1);
  const query = {
    groupId: groupId || undefined,
    state,
    withinDays: state === 'expiring' ? withinDays : undefined,
    page: pageNo,
    pageSize: PAGE_SIZE,
  };
  const q = useQuery({
    queryKey: ['w18', 'compliance', 'report', query],
    queryFn: () => api.get(`${COMPLIANCE}/report`, { query, schema: ComplianceReport }),
    placeholderData: keepPreviousData,
  });
  return (
    <div>
      <PageHeader
        title={t('w18.report.title')}
        description={t('w18.report.description')}
        actions={
          <ExportButton
            path={`${COMPLIANCE}/report/export`}
            query={{
              groupId: groupId || undefined,
              state,
              withinDays: state === 'expiring' ? withinDays : undefined,
            }}
            fileName="compliance"
          />
        }
      />
      <FilterBar columns="sm:grid-cols-3">
        <SelectField
          label={t('w18.report.group')}
          value={groupId}
          onChange={(e) => {
            setGroupId(e.target.value);
            setPageNo(1);
          }}
        >
          <option value="">{t('w18.report.allGroups')}</option>
          {(groups.data ?? []).map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
        </SelectField>
        <SelectField
          label={t('w18.report.show')}
          value={state}
          onChange={(e) => {
            setState(e.target.value as State);
            setPageNo(1);
          }}
        >
          <option value="problems">{t('w18.report.show.problems')}</option>
          <option value="expiring">{t('w18.report.show.expiring')}</option>
          <option value="all">{t('w18.report.show.all')}</option>
        </SelectField>
        {state === 'expiring' ? (
          <TextField
            type="number"
            min={1}
            max={365}
            label={t('w18.report.within')}
            value={withinDays}
            onChange={(e) => {
              setWithinDays(Math.max(1, Math.min(365, Number(e.target.value) || 30)));
              setPageNo(1);
            }}
          />
        ) : null}
      </FilterBar>
      {q.isError ? (
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
      ) : !q.data ? (
        <Spinner />
      ) : (
        <>
          <TableWrap caption={t('w18.report.title')}>
            <thead className="bg-slate-50">
              <tr>
                <Th>{t('w18.report.person')}</Th>
                <Th>{t('w18.report.groupCol')}</Th>
                <Th>{t('w18.report.document')}</Th>
                <Th>{t('common.status')}</Th>
                <Th>{t('w18.report.expiry')}</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {q.data.items.length === 0 ? <EmptyRow cols={5} /> : null}
              {q.data.items.map((r, i) => (
                <tr key={`${r.memberId}-${r.typeName}-${i}`}>
                  <td className="px-4 py-2.5 font-medium text-slate-900">{r.memberName}</td>
                  <td className="px-4 py-2.5">{r.groupName}</td>
                  <td className="px-4 py-2.5">{r.typeName}</td>
                  <td className="px-4 py-2.5">
                    <Badge tone={STATE_TONE[r.state] ?? 'neutral'}>{t(`w18.doc.state.${r.state}`)}</Badge>
                  </td>
                  <td className="px-4 py-2.5">
                    {r.expiryDate ? (
                      <>
                        {formatDay(r.expiryDate)}
                        <span className="ml-2 text-xs text-slate-600">
                          {daysUntil(r.expiryDate) < 0
                            ? t('w18.report.daysAgo', {
                                days: -daysUntil(r.expiryDate),
                              })
                            : t('w18.report.inDays', {
                                days: daysUntil(r.expiryDate),
                              })}
                        </span>
                      </>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
          <Pager page={pageNo} pageSize={PAGE_SIZE} total={q.data.total} onPage={setPageNo} />
        </>
      )}
    </div>
  );
}
